import React, { useEffect, useRef, useState } from 'react';
import { Grid, GridCol } from '@naf/grid';
import { Text, TextVariant } from '@naf/text';
import { Link } from '@naf-simba/elements-links';
import type { SanityBlock } from '@sanity/client';
import { breakpoints } from '@naf/theme';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { useMediaQuery } from '@hooks/useMediaQuery';
import S from '@styles/articles/StyledArticle';
import { QuoteBlock } from '@components/block-content/types/components/Quote/Quote';
import { AccordionBlock } from '@components/block-content/types/components/Accordion/Accordion';
import BreadCrumb from '@components/breadcrumb/BreadCrumb';
import { LayoutWithMainContent } from '@components/layout/Layout';
import { ProductArticleType } from '@appTypes/articleType';
import { useDocumentUrl } from '@hooks/Documents/useDocumentUrl';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import { useResponsiveImage } from '@hooks/useResponsiveImage';
import { MainEntryCard } from '@components/entry-cards/MainEntryCard';
import { CampaignBlock } from '@components/block-content/types/components/CampaignBlock/CampaignBlock';
import ResponsiveImage from '@components/image/ResponsiveImage';
import { IMAGE_SIZES } from '@components/image/ResponsiveImage.sizes';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import FeedbackSurvey from '@components/feedbacksurvey/FeedbackSurvey';
import { EventPayload } from '@appTypes/GTM/EventPayload';
import { ThreeColumnBlock } from '@components/block-content/types/components/ThreeColumnBlockSerializer/ThreeColumnBlockSerializer';
import SocialProofBlock from '@components/block-content/types/components/SocialProof/SocialProof';
import BlockContent from '@components/block-content/BlockContent';
import { ButtonContainer, HeaderText, ImageGridColumn } from './ProductArticle.styles';
import BenefitList from '../BenefitArticle/NewRelatedBenefits/BenefitList/BenefitList';
import { ProductArticleButton } from './ProductArticleButton';

interface Props {
  productArticle: ProductArticleType;
}

const ProductArticle = ({ productArticle }: Props) => {
  const url = useDocumentUrl(productArticle?.slug);
  // Onload Tracking
  const dataLayer = useGTMDataLayer();
  const [isTrackingEventSent, setIsTrackingEventSent] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.m})`);

  useEffect(() => {
    if (productArticle.onloadTracking && dataLayer && !isTrackingEventSent) {
      const eventPayload: EventPayload = {
        event: productArticle.onloadTracking.event,
      };
      if (productArticle.onloadTracking.payloadReference === 'ecommerce' && productArticle.ecommerce) {
        eventPayload.ecommerce = productArticle.ecommerce;
      }
      dataLayer.push(eventPayload);
      setIsTrackingEventSent(true);
    }
  }, [productArticle, isTrackingEventSent, dataLayer]);

  const {
    name,
    header,
    body,
    image,
    relatedProduct,
    mainEntries,
    productSummary,
    threeColumn,
    quote,
    socialProof,
    campaignBlock,
    seoConfig,
    slug,
    metaDisplay,
  } = productArticle || {};

  const cldImage = useResponsiveImage({ imageId: image?.publicId, initialDimensions: { width: 1200, height: 630 } });
  const windowPath = useWindowLocation();
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const seoTitle = seoConfig?.title ? seoConfig.title : name;
  const schemaImage = cldImage && cldImage.toURL();

  return (
    <LayoutWithMainContent
      title={header || name}
      description={seoConfig?.introduction}
      imgUrl={schemaImage || ''}
      url={windowPath}
      gtmArgs={{
        page_type: 'ProductArticlePage',
        pageCategory: `${url}`,
        contentId: slug,
      }}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoTitle={seoTitle}
      seoDescription={seoConfig?.introduction}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.Product,
        product: { title: relatedProduct?.productHeaderText },
      }}
    >
      <S.Wrapper>
        <Grid>
          <GridCol s="12" m="12" l="6" xl="6">
            <BreadCrumb />
            <HeaderText tag="h1" variant={TextVariant.Header1}>
              {header || name}
            </HeaderText>
          </GridCol>
          {/* Empty column to align with image */}
          <GridCol s="0" m="0" l="6" xl="6">
            <div />
          </GridCol>
          <GridCol s="12" m="12" l="6" xl="6">
            <BlockContent value={body} />
          </GridCol>
          {image && !isMobile && (
            <ImageGridColumn s="12" m="12" l="6" xl="6">
              <S.FullHeightImageColWrapper ref={imageWrapperRef}>
                <ResponsiveImage
                  imageId={image.publicId}
                  altText={image.altText || image.caption}
                  sizes={IMAGE_SIZES.twoColumn}
                />
              </S.FullHeightImageColWrapper>
            </ImageGridColumn>
          )}
          {relatedProduct && (
            <GridCol s="12" m="12" l="6" xl="6">
              {relatedProduct.productHeaderText && (
                <S.NoMarginText tag="h3" variant={TextVariant.Header3}>
                  {relatedProduct.productHeaderText}
                </S.NoMarginText>
              )}
              {relatedProduct.campaignText && (
                <S.NoMarginText variant={TextVariant.Header3}>{relatedProduct.campaignText}</S.NoMarginText>
              )}
              {relatedProduct.productText && (
                <S.RelatedProductText variant={TextVariant.ArticleText}>
                  {relatedProduct.productText}
                </S.RelatedProductText>
              )}
              <ButtonContainer>
                {relatedProduct.ctaButtonLink?.url && relatedProduct.ctaButtonLink?.text && (
                  <ProductArticleButton
                    productArticle={productArticle}
                    url={relatedProduct.ctaButtonLink.url}
                    text={relatedProduct.ctaButtonLink.text}
                  />
                )}
                {relatedProduct.ctaSecondaryButtonLink?.url && relatedProduct.ctaSecondaryButtonLink?.text && (
                  <ProductArticleButton
                    productArticle={productArticle}
                    url={relatedProduct.ctaSecondaryButtonLink.url}
                    text={relatedProduct.ctaSecondaryButtonLink.text}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </ButtonContainer>
            </GridCol>
          )}

          {mainEntries && mainEntries?.length > 0 && (
            <S.SpacedGridRow>
              {mainEntries.map((entry) => (
                <MainEntryCard key={`${entry.title}`} entry={entry} col="4" />
              ))}
            </S.SpacedGridRow>
          )}
          {productSummary && productSummary.summarylist && productSummary.summarylist.length > 0 && (
            <S.SpacedGridRow>
              <GridCol s="12" m="12" l="8" xl="8">
                <S.ColoredBox>
                  <S.InternalColoredBox>
                    {productSummary.header && (
                      <Text tag="h2" variant={TextVariant.Header2}>
                        {productSummary.header}
                      </Text>
                    )}
                    {productSummary.summarylist.map(
                      (accordion: { text: SanityBlock[]; title: string; type: string }) => (
                        <AccordionBlock key={accordion.title} value={accordion} />
                      ),
                    )}
                  </S.InternalColoredBox>
                </S.ColoredBox>
              </GridCol>
            </S.SpacedGridRow>
          )}
          {threeColumn && (
            <S.SpacedGridRow>
              <S.ThreeColumnCol s="12" m="12" l="12" xl="12">
                <ThreeColumnBlock node={threeColumn} />
              </S.ThreeColumnCol>
            </S.SpacedGridRow>
          )}
          {quote && quote.text && (
            <S.SpacedGridRow>
              <S.QuoteCol s="12" m="12" l="12" xl="12">
                <QuoteBlock value={quote} />
              </S.QuoteCol>
            </S.SpacedGridRow>
          )}
          {socialProof && socialProof.text && (
            <S.SpacedGridRow>
              <S.QuoteCol s="12" m="12" l="12" xl="12">
                <SocialProofBlock value={socialProof} />
              </S.QuoteCol>
            </S.SpacedGridRow>
          )}
          {campaignBlock && (
            <GridCol s="12" m="12" l="12" xl={campaignBlock.illustration ? '12' : '11'}>
              <CampaignBlock value={campaignBlock} />
            </GridCol>
          )}
          {productArticle.membershipBenefits && (
            <GridCol s="12" m="12" l="12" xl="12">
              <S.ReadMoreWrapper>
                <Text variant={TextVariant.Header2}>Medlemsfordeler</Text>
                <Link text="Se alle" to="/medlemskap/medlemsfordeler" />
              </S.ReadMoreWrapper>
              <BenefitList benefitItems={productArticle.membershipBenefits} />
            </GridCol>
          )}
          {!metaDisplay.hideForm && (
            <GridCol s="12" m="12" l="8" xl="8">
              <FeedbackSurvey contentUrl={windowPath} />
            </GridCol>
          )}
        </Grid>
      </S.Wrapper>
    </LayoutWithMainContent>
  );
};

export default ProductArticle;
